<template>
  <label
    class="checkbox-toggle"
    :class="{
      'checkbox-toggle--checked': value,
      'checkbox-toggle--disabled': disabled,
      'checkbox-toggle--rebrand': rebrand,
      'checkbox-toggle--pre-label': preLabel,
    }"
    :for="id"
    role="switch"
    tabindex="0"
    :aria-checked="value"
  >
    <span
      v-if="preLabel"
      class="ct-label"
    >
      {{ label }}
      <span
        v-if="required"
        class="mandatory"
        >*</span
      >
    </span>
    <input
      v-model="model"
      v-bind="props"
      type="checkbox"
      data-hj-suppress
      @change="onInput"
    />
    <div class="ct-button" />
    <span
      v-if="!preLabel"
      class="ct-label"
    >
      {{ label }}
      <span
        v-if="required"
        class="mandatory"
        >*</span
      >
    </span>
  </label>
</template>

<script setup lang="ts">
import type { InputFieldProps } from '@/mixins/InputFieldAttributes';
import type { HTMLInputEvent } from '@/types/forms';

const props = defineProps<
  Omit<InputFieldProps, 'name'> & {
    label: string;
    preLabel?: boolean;
    rebrand?: boolean;
  }
>();

const emit = defineEmits(['input']);
const model = defineModel<boolean>('value');

const onInput = (event: HTMLInputEvent) => {
  emit('input', event.target.checked);
};
</script>

<style lang="scss" scoped>
.checkbox-toggle {
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);

  .ct-button {
    position: relative;
    display: inline-block;
    width: 42px;
    height: 24px;
    background-color: palette(grey, light);
    border: 1px solid palette(grey, light);
    border-radius: 12px;
    transition: all 0.2s ease-in-out;

    &:after {
      content: '';
      position: absolute;
      display: inline-block;
      width: 22px;
      height: 22px;
      background-color: $color-general-background-neutral-default;
      left: 0;
      top: 0;
      border-radius: 100%;
    }
  }

  input {
    display: none;
  }

  .ct-label {
    margin-left: math.div($pad, 2);
    font-size: map-get($font-sizes, 'm');
  }

  &.checkbox-toggle--checked {
    .ct-button {
      background-color: $color-brand-fnmz-green;
      border-color: $color-brand-fnmz-green;

      &:after {
        left: auto;
        right: 0;
      }
    }
  }

  &.checkbox-toggle--disabled {
    pointer-events: none;

    .ct-button {
      background-color: palette(sand);
      border-color: palette(grey, light);
    }
  }

  &.checkbox-toggle--rebrand {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;

    .ct-label {
      font-size: map-get($font-sizes, 'l');
      width: calc(100% - 70px);
      margin-right: 0;
    }

    .ct-button {
      position: relative;
      display: block;
      width: 51px;
      height: 31px;
      border-radius: 31px;
      background-color: palette(grey, metallic);
      transition: all 0.2s ease;
      transform-origin: 20% center;
      cursor: pointer;
      border: 0;

      &:before {
        content: '';
        position: absolute;
        display: block;
        width: 27px;
        height: 27px;
        top: 2px;
        left: 2px;
        border-radius: map-get($border-radiuses, 'half');
        background-color: $color-general-background-neutral-default;
        box-shadow:
          0 3px 1px 0 rgba(51, 51, 51, 0.06),
          0 3px 8px 0 rgba(51, 51, 51, 0.15);
        border: 0.5px solid rgba(51, 51, 51, 0.04);
        transition: all 0.2s ease;
      }

      &:after {
        display: none;
      }
    }

    &.checkbox-toggle--checked {
      .ct-button {
        background-color: palette(orientation, green);
        border-color: palette(orientation, green);

        &:before {
          transform: translateX(math.div($ui-height, 2));
        }
      }
    }
  }

  &.checkbox-toggle--pre-label {
    .ct-label {
      margin: 0 math.div($pad, 2) 0 0;
    }
  }
}
</style>
